import { useEffect, useState } from 'react';
import Select from 'react-select';
import { logEntryApi, stationsApi } from '../api/logbookApi';
import { useOffline } from '../context/OfflineContext';
import './NewEntryModal.css';

const CATEGORIES = [
  { value: 'other',    label: 'Egyéb / parkoló', icon: 'ti-parking' },
  { value: 'pickup',   label: 'Felrakó',          icon: 'ti-package' },
  { value: 'delivery', label: 'Lerakó',           icon: 'ti-package-export' },
  { value: 'customs',  label: 'Vámkezelés',       icon: 'ti-building-bank' },
  { value: 'refuel',   label: 'Tankolás',         icon: 'ti-gas-station' },
];

const STATION_CATEGORIES = ['pickup', 'delivery', 'customs'];
const FREE_TEXT_CATEGORIES = ['other', 'refuel'];

const COUNTRIES = [
  { value: 'AD', label: 'AD – Andorra' },
  { value: 'AL', label: 'AL – Albánia' },
  { value: 'AT', label: 'AT – Ausztria' },
  { value: 'BA', label: 'BA – Bosznia-Hercegovina' },
  { value: 'BE', label: 'BE – Belgium' },
  { value: 'BG', label: 'BG – Bulgária' },
  { value: 'BY', label: 'BY – Fehéroroszország' },
  { value: 'CH', label: 'CH – Svájc' },
  { value: 'CZ', label: 'CZ – Csehország' },
  { value: 'DE', label: 'DE – Németország' },
  { value: 'DK', label: 'DK – Dánia' },
  { value: 'EE', label: 'EE – Észtország' },
  { value: 'ES', label: 'ES – Spanyolország' },
  { value: 'FI', label: 'FI – Finnország' },
  { value: 'FR', label: 'FR – Franciaország' },
  { value: 'GB', label: 'GB – Nagy-Britannia' },
  { value: 'GE', label: 'GE – Grúzia' },
  { value: 'GR', label: 'GR – Görögország' },
  { value: 'HR', label: 'HR – Horvátország' },
  { value: 'HU', label: 'HU – Magyarország' },
  { value: 'IE', label: 'IE – Írország' },
  { value: 'IS', label: 'IS – Izland' },
  { value: 'IT', label: 'IT – Olaszország' },
  { value: 'LT', label: 'LT – Litvánia' },
  { value: 'LU', label: 'LU – Luxemburg' },
  { value: 'LV', label: 'LV – Lettország' },
  { value: 'MC', label: 'MC – Monaco' },
  { value: 'MT', label: 'MT – Málta' },
  { value: 'NL', label: 'NL – Hollandia' },
  { value: 'NO', label: 'NO – Norvégia' },
  { value: 'PL', label: 'PL – Lengyelország' },
  { value: 'PT', label: 'PT – Portugália' },
  { value: 'RO', label: 'RO – Románia' },
  { value: 'RS', label: 'RS – Szerbia' },
  { value: 'RU', label: 'RU – Oroszország' },
  { value: 'SE', label: 'SE – Svédország' },
  { value: 'SI', label: 'SI – Szlovénia' },
  { value: 'SK', label: 'SK – Szlovákia' },
  { value: 'TR', label: 'TR – Törökország' },
];

const selectStyles = {
  control: (base, state) => ({
    ...base,
    backgroundColor: '#ffffff',
    borderColor: state.isFocused ? '#2bb344' : '#e1e8e2',
    borderRadius: 10,
    padding: '4px 2px',
    boxShadow: 'none',
    fontSize: 16,
    '&:hover': { borderColor: '#2bb344' },
  }),
  menu: (base) => ({
    ...base,
    backgroundColor: '#ffffff',
    border: '1px solid #e1e8e2',
    borderRadius: 10,
    overflow: 'hidden',
    zIndex: 20,
    boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 9999,
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected ? '#e8f9eb' : state.isFocused ? '#f7faf7' : 'transparent',
    color: state.isSelected ? '#1f8c34' : '#1c241e',
    cursor: 'pointer',
    fontSize: 15,
  }),
  singleValue: (base) => ({ ...base, color: '#1c241e' }),
  input: (base) => ({ ...base, color: '#1c241e' }),
  placeholder: (base) => ({ ...base, color: '#8a958c' }),
  indicatorSeparator: () => ({ display: 'none' }),
  dropdownIndicator: (base) => ({ ...base, color: '#8a958c' }),
};

function nowLocal() {
  const now = new Date();
  now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
  return now.toISOString().slice(0, 16);
}

export default function NewEntryModal({ logbookId, onClose, onCreated, prefilled }) {
  const { isOnline, savePendingEntry } = useOffline();

  const [category, setCategory] = useState(prefilled?.category || '');
  const [city, setCity] = useState(prefilled?.city || '');
  const [country, setCountry] = useState(
    prefilled?.country
      ? COUNTRIES.find(c => c.value === prefilled.country) || null
      : null
  );
  const [selectedStation, setSelectedStation] = useState(null);
  const [stations, setStations] = useState([]);
  const [stationsLoading, setStationsLoading] = useState(false);
  const [arrival, setArrival] = useState(nowLocal());
  const [departure, setDeparture] = useState('');
  const [km, setKm] = useState('');
  const [diesel, setDiesel] = useState('');
  const [adblue, setAdblue] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [gpsLoading, setGpsLoading] = useState(false);
  const [gpsError, setGpsError] = useState('');

  const needsStation = STATION_CATEGORIES.includes(category);
  const isFreeText = FREE_TEXT_CATEGORIES.includes(category);

  useEffect(() => {
    if (needsStation) {
      setStationsLoading(true);
      stationsApi.list(category)
        .then(res => {
          const options = res.data.map(s => ({
            value: s.id,
            label: s.city,
            company: s.company,
            country: s.country,
            city: s.city,
          }));
          setStations(options);

          // Automatikus kiválasztás prefilled alapján
          if (prefilled?.orderstation_id) {
            const found = options.find(o => o.value === prefilled.orderstation_id);
            if (found) {
              setSelectedStation(found);
            } else {
              // Ha nem találja ID alapján, keresés városnév alapján
              const byCity = options.find(o => o.city === prefilled.city);
              if (byCity) setSelectedStation(byCity);
            }
          }
        })
        .catch(() => setError('Nem sikerült betölteni az állomásokat.'))
        .finally(() => setStationsLoading(false));
    }
    if (!prefilled) {
      setSelectedStation(null);
      setCity('');
      setCountry(null);
    }
    setGpsError('');
  }, [category]);

  const handleGpsLocation = () => {
    if (!navigator.geolocation) {
      setGpsError('A böngésző nem támogatja a helymeghatározást.');
      return;
    }
    setGpsLoading(true);
    setGpsError('');
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const { latitude, longitude } = position.coords;
          const res = await fetch(
            `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=hu`
          );
          const data = await res.json();
          const cityName =
            data.locality ||
            data.localityInfo?.administrative?.find(a => a.adminLevel === 8)?.name ||
            data.city ||
            '';
          const countryCode = data.countryCode || '';
          if (cityName) setCity(cityName);
          if (countryCode) {
            const found = COUNTRIES.find(c => c.value === countryCode);
            if (found) setCountry(found);
          }
        } catch {
          setGpsError('Nem sikerült lekérdezni a helyadatokat.');
        } finally {
          setGpsLoading(false);
        }
      },
      () => {
        setGpsError('Helymeghatározás sikertelen. Engedélyezd a helyhozzáférést!');
        setGpsLoading(false);
      },
      { timeout: 10000, enableHighAccuracy: true }
    );
  };

  const handleSave = async () => {
    setError('');
    if (!category) { setError('Kategória választása kötelező.'); return; }
    if (!km) { setError('Km óra állás megadása kötelező.'); return; }
    if (needsStation && !selectedStation) { setError('Állomás választása kötelező.'); return; }
    if (isFreeText && !city.trim()) { setError('Város megadása kötelező.'); return; }
    if (category === 'refuel' && !diesel) { setError('Dízel mennyiség megadása kötelező.'); return; }

    const payload = {
      logbook: parseInt(logbookId),
      category,
      km: parseInt(km),
      arrival: arrival || null,
      departure: departure || null,
      city: needsStation ? selectedStation.city : city.trim(),
      country: needsStation ? (selectedStation.country || null) : (country?.value || null),
      orderstation: needsStation ? selectedStation.value : null,
      diesel: diesel || null,
      adblue: adblue || null,
    };

    setSaving(true);
    try {
      if (isOnline) {
        await logEntryApi.create(payload);
      } else {
        await savePendingEntry(payload);
      }
      onCreated();
    } catch {
      setError('Nem sikerült menteni a bejegyzést.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box entry-modal-box" onClick={e => e.stopPropagation()}>

        <div className="modal-header">
          <h3><i className="ti ti-plus"></i> Új bejegyzés</h3>
          {!isOnline && (
            <span className="offline-indicator">
              <i className="ti ti-wifi-off"></i> Offline
            </span>
          )}
          <button className="modal-close" onClick={onClose}>
            <i className="ti ti-x"></i>
          </button>
        </div>

        <div className="modal-body">

          {/* Kategória választó */}
          <label>Kategória</label>
          <div className="category-grid">
            {CATEGORIES.map(cat => (
              <button
                key={cat.value}
                className={`category-btn ${category === cat.value ? 'selected' : ''}`}
                onClick={() => setCategory(cat.value)}
              >
                <i className={`ti ${cat.icon}`}></i>
                <span>{cat.label}</span>
              </button>
            ))}
          </div>

          {/* Állomás dropdown */}
          {needsStation && (
            <>
              <label>Állomás</label>
              {!isOnline ? (
                <>
                  <div className="offline-field-notice">
                    <i className="ti ti-wifi-off"></i>
                    Offline módban kézi városnév szükséges
                  </div>
                  <input
                    type="text"
                    className="entry-input"
                    placeholder="Városnév"
                    value={city}
                    onChange={e => setCity(e.target.value)}
                  />
                </>
              ) : (
                <Select
                  options={stations}
                  value={selectedStation}
                  onChange={setSelectedStation}
                  isLoading={stationsLoading}
                  placeholder="Keress állomásra..."
                  noOptionsMessage={() => 'Nincs elérhető állomás'}
                  styles={selectStyles}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  formatOptionLabel={(option) => (
                    <div>
                      <span style={{ fontWeight: 700, color: '#1c241e' }}>{option.city}</span>
                      {option.country && (
                        <span style={{ color: '#2473b8', fontSize: 13, fontWeight: 600 }}> {option.country}</span>
                      )}
                      {option.company && (
                        <span style={{ color: '#8a958c', fontSize: 13 }}> – {option.company}</span>
                      )}
                    </div>
                  )}
                />
              )}
            </>
          )}

          {/* Szabad szöveges város + ország + GPS */}
          {isFreeText && (
            <>
              <label>Város</label>
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  type="text"
                  className="entry-input"
                  placeholder="pl. Budapest"
                  value={city}
                  onChange={e => setCity(e.target.value)}
                  style={{ flex: 1 }}
                />
                <button
                  type="button"
                  className="btn-gps"
                  onClick={handleGpsLocation}
                  disabled={gpsLoading}
                  title="Helymeghatározás"
                >
                  {gpsLoading
                    ? <i className="ti ti-loader-2 spin"></i>
                    : <i className="ti ti-current-location"></i>
                  }
                </button>
              </div>
              {gpsError && <p className="modal-error">{gpsError}</p>}
              <label>Ország</label>
              <Select
                options={COUNTRIES}
                value={country}
                onChange={setCountry}
                placeholder="Válassz országot..."
                noOptionsMessage={() => 'Nincs találat'}
                styles={selectStyles}
                menuPortalTarget={document.body}
                menuPosition="fixed"
                isClearable
              />
            </>
          )}

          {/* Km óra */}
          <label>Km óra állása</label>
          <input
            type="number"
            inputMode="numeric"
            pattern="[0-9]*"
            className="entry-input"
            placeholder="pl. 125430"
            value={km}
            onChange={e => setKm(e.target.value)}
          />

          {/* Idők */}
          <div className="entry-time-row">
            <div className="entry-time-col">
              <label>Érkezés</label>
              <input
                type="datetime-local"
                className="entry-input"
                value={arrival}
                onChange={e => setArrival(e.target.value)}
              />
            </div>
            <div className="entry-time-col">
              <label>Távozás</label>
              <input
                type="datetime-local"
                className="entry-input"
                value={departure}
                onChange={e => setDeparture(e.target.value)}
              />
            </div>
          </div>

          {/* Tankolás mezők */}
          {category === 'refuel' && (
            <div className="entry-time-row">
              <div className="entry-time-col">
                <label>Dízel (liter)</label>
                <input
                  type="number"
                  inputMode="decimal"
                  pattern="[0-9]*"
                  className="entry-input"
                  placeholder="pl. 320"
                  value={diesel}
                  onChange={e => setDiesel(e.target.value)}
                />
              </div>
              <div className="entry-time-col">
                <label>AdBlue (liter)</label>
                <input
                  type="number"
                  inputMode="decimal"
                  pattern="[0-9]*"
                  className="entry-input"
                  placeholder="pl. 20"
                  value={adblue}
                  onChange={e => setAdblue(e.target.value)}
                />
              </div>
            </div>
          )}

          {error && <p className="modal-error">{error}</p>}
        </div>

        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose}>Mégse</button>
          <button className="btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Mentés...' : isOnline ? 'Bejegyzés mentése' : 'Mentés offline'}
          </button>
        </div>
      </div>
    </div>
  );
}